"use client";
import React, { useState, useEffect } from "react";
import { AspectRatio } from "~/components/ui/aspect-ratio";
import Image from "next/image";
import { Button } from "~/components/ui/button";
import { ArrowLongRight } from "~/components/icons";
import { LinkTrackers } from "~/components/reusables/link-trackers";
import { usePathname } from "next/navigation";
import StrapiBlocksRenderer from "~/components/reusables/strapi-blocks-renderer";

export function BannerClient({ banners }) {
  const [isHovered, setIsHovered] = useState(false);

  const pathname = usePathname();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  if (!mounted) return null; // avoid hydration mismatch

  const banner = [...(banners["*"] || []), ...(banners[pathname] || [])];

  if (!banner.length) return null;

  return (
    <>
      {banner?.[0] && (
        <div
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className={``}
          style={{
            background:
              banner[0].bgColor?.gradientType === "solid"
                ? isHovered
                  ? banner[0].bgColor?.hoverColorFrom
                  : banner[0].bgColor?.colorFrom
                : `linear-gradient(${
                    banner[0].bgColor?.gradientType === "left-to-right"
                      ? "to right"
                      : banner[0].bgColor?.gradientType === "right-to-left"
                        ? "to left"
                        : banner[0].bgColor?.gradientType === "top-to-bottom"
                          ? "to bottom"
                          : banner[0].bgColor?.gradientType === "bottom-to-top"
                            ? "to top"
                            : "to right"
                  }, ${isHovered ? banner[0].bgColor?.hoverColorFrom : banner[0].bgColor?.colorFrom}, ${isHovered ? banner[0].bgColor?.hoverColorTo : banner[0].bgColor?.colorTo})`,
            color: banner[0].bgColor?.textColor,
          }}
        >
          <LinkTrackers
            segmentMsg={banner[0].segmentMsg.segmentMsg}
            segmentOpt={banner[0].segmentMsg.segmentOpt}
            href={banner[0].ctaBtn.url}
            target={banner[0].ctaBtn.external ? "_blank" : "_self"}
            aria-label="Learn more about IOA"
            className="lg:font-btn group flex w-full cursor-pointer items-center gap-1 py-5  text-xs md:py-6"
          >
            <div
              className={`container flex min-h-14 items-center ${banner[0].ctaBtn.variant == "icon" ? "justify-start" : "justify-between"} gap-3`}
            >
              <div className="flex items-center gap-3 md:gap-6">
                <div className="self-start lg:self-center">
                  <div className="flex flex-col items-center gap-2 self-start pr-3 md:flex-row md:gap-5 lg:self-center lg:pr-0">
                    {banner[0].logo.length ? (
                      banner[0].logo.map((logo, index) => {
                        const iconURL = logo.image.url;
                        const icon = logo.image;
                        return (
                          <div
                            key={`logo-${index}`}
                            className="mt-1 h-full lg:mt-0"
                            style={{
                              maxHeight:
                                icon.width / icon.height > 5 ? "26px" : "42px",
                              width: `${(icon.width / icon.height) * (icon.width / icon.height > 5 ? 26 : 42)}px`,
                            }}
                          >
                            <AspectRatio ratio={icon.width / icon.height}>
                              <Image
                                src={iconURL}
                                alt="logo"
                                width={icon.width}
                                height={icon.height}
                                unoptimized
                              ></Image>
                            </AspectRatio>
                          </div>
                        );
                      })
                    ) : (
                      <div className="mt-1 h-full w-20 md:mr-6 md:w-24 lg:mt-0 lg:w-[120px]">
                        <AspectRatio ratio={179 / 74}>
                          <Image
                            src={
                              "https://outshift-headless-cms-s3.s3.us-east-2.amazonaws.com/_logo-Outshift_1C-WHITE%402x%202.png"
                            }
                            alt="Outshift logo"
                            fill
                            unoptimized
                          ></Image>
                        </AspectRatio>
                      </div>
                    )}
                  </div>
                </div>
                {banner[0].description && (
                  <StrapiBlocksRenderer content={banner[0].description} />
                )}
              </div>
              {banner[0].ctaBtn.variant == "icon" ? (
                <div className="ml-3 group-hover:animate-bounce-x lg:ml-5">
                  <ArrowLongRight size={35} />
                </div>
              ) : (
                <>
                  <Button
                    variant={banner[0].ctaBtn?.variant == "outline" ? "outline" : "secondary"}
                    className="align-end hidden min-w-32 px-0 font-medium group-hover:bg-white group-hover:text-slate-900 md:px-3 lg:flex"
                    style={
                      banner[0].ctaBtn?.variant !== "outline"
                        ? {
                            background:
                              banner[0].ctaBtnBG?.gradientType === "solid"
                                ? isHovered
                                  ? banner[0].ctaBtnBG?.hoverColorFrom
                                  : banner[0].ctaBtnBG?.colorFrom
                                : `linear-gradient(${
                                    banner[0].ctaBtnBG?.gradientType ===
                                    "left-to-right"
                                      ? "to right"
                                      : banner[0].ctaBtnBG?.gradientType ===
                                          "right-to-left"
                                        ? "to left"
                                        : banner[0].ctaBtnBG?.gradientType ===
                                            "top-to-bottom"
                                          ? "to bottom"
                                          : banner[0].ctaBtnBG?.gradientType ===
                                              "bottom-to-top"
                                            ? "to top"
                                            : "to right"
                                  }, ${isHovered ? banner[0].ctaBtnBG?.hoverColorFrom : banner[0].ctaBtnBG?.colorFrom}, ${isHovered ? banner[0].ctaBtnBG?.hoverColorTo : banner[0].ctaBtnBG?.colorTo})`,
                            color: banner[0].ctaBtnBG?.textColor,
                          }
                        : undefined
                    }
                  >
                    {banner[0].ctaBtn.cta ? (
                      <p className="font-h6">{banner[0].ctaBtn.cta}</p>
                    ) : (
                      <div className="ml-1">
                        <ArrowLongRight size={35} />
                      </div>
                    )}
                  </Button>
                  <div className="ml-1 lg:hidden">
                    <ArrowLongRight size={35} />
                  </div>
                </>
              )}
            </div>
          </LinkTrackers>
        </div>
      )}
    </>
  );
}
